import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, X, Clock, MessageSquare, ShieldAlert, Package,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

// Store staff roles that can use Quick Actions
const STORE_STAFF_ROLES = [
  'store_manager',
  'assistant_manager', 
  'grocery_staff',
  'checkout_staff',
  'staff',
  'casual'
];

const QUICK_ACTIONS = [
  {
    id: 'expiry',
    label: 'Expiry',
    description: 'Report expiring products',
    icon: Clock,
    path: '/expiry-report',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-600'
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Submit customer feedback',
    icon: MessageSquare,
    path: '/feedback/submit',
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600'
  },
  {
    id: 'incident',
    label: 'Incident',
    description: 'Report an incident',
    icon: ShieldAlert,
    path: '/incidents/report',
    color: 'from-red-500 to-pink-500',
    bgColor: 'bg-red-100',
    textColor: 'text-red-600'
  },
  {
    id: 'demand',
    label: 'Demand',
    description: 'Customer product request',
    icon: Package,
    path: '/customer-demand',
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-100',
    textColor: 'text-green-600'
  }
];

export default function QuickActionButton() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Only show for store staff roles
  if (!user || !STORE_STAFF_ROLES.includes(user.role)) {
    return null;
  }

  const handleAction = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[998] backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Quick Action Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-36 md:bottom-24 right-4 z-[999] w-72"
            data-testid="quick-action-menu"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-400" />
                    <span className="font-semibold text-white">Quick Action</span>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-2">
                {QUICK_ACTIONS.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <motion.button
                      key={action.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleAction(action.path)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-all group"
                      data-testid={`quick-action-${action.id}`}
                    >
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br ${action.color} text-white shadow-lg`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-slate-800">{action.label}</p>
                        <p className="text-xs text-slate-500">{action.description}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all" />
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-4 md:bottom-6 z-[999] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${
          isOpen 
            ? 'bg-slate-800 rotate-45' 
            : 'bg-gradient-to-br from-yellow-400 to-orange-500 hover:shadow-orange-300/50 hover:scale-110'
        }`}
        whileTap={{ scale: 0.9 }}
        data-testid="quick-action-fab"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <Zap className="h-6 w-6 text-white" />
        )}
      </motion.button>
    </>
  );
}
